import classNames from 'classnames';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { animated } from 'react-spring';
import styled from 'styled-components';

import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { Vertical } from 'atoms/layout/flex';
import { WithScrollButtons } from 'atoms/navigation/WithScrollButtons';
import { useScroll } from 'hooks/animations/useScroll';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useDebouncedCallback } from 'hooks/useDebouncedCallback';
import { laptopAndAbove } from 'style/mediaQuery';
import { hideScrollbar } from 'style/utils';

const Wrapper = styled(Vertical)`
  width: 100%;
  @media ${laptopAndAbove} {
    width: unset;
  }
`;

const ITEM_GAP = 24;
const ITEM_GAP_IN_UNIT = 3;

const ScrollWrapper = styled.div`
  --gap: ${ITEM_GAP}px;
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  gap: var(--gap);
  ${hideScrollbar}
  padding: 0 calc(var(--item-width) / 2 + var(--gap));
  @media ${laptopAndAbove} {
    padding: 0 calc(50% - var(--item-width) / 2);
  }
`;

const ItemWrapper = styled(animated(ButtonBase))`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--triple-unit);
  scroll-snap-align: center;
  width: var(--item-width);
  min-width: var(--item-width);
  will-change: transform, filter, opacity;
  & > img {
    width: 100%;
    object-fit: contain;
    aspect-ratio: var(--item-aspect-ratio);
    @media ${laptopAndAbove} {
      max-height: unset;
    }
  }
`;

const Shadow = styled.div`
  width: 100%;
  height: calc(6 * var(--unit));
  border-radius: 50%;
  background: var(--c-black);
  opacity: 0.56;
  filter: blur(10px);
  margin-bottom: var(--double-unit);
`;
type Props = {
  items: { key: string; pictureUrl: string }[];
  selectedItemKey: string;
  onScrollToItem: (key: string) => void;
  onItemClick?: (key: string) => void;
  withShadow?: boolean;
  itemAspectRatio?: CSSProperties['aspectRatio'];
  itemMaxWidth?: number;
  children?: (props: { pictureUrl: string; key: string }) => React.ReactNode;
};

export const AnimatedScrollable = ({
  items,
  selectedItemKey,
  onScrollToItem,
  onItemClick,
  withShadow = true,
  children,
  itemAspectRatio = 0.6334,
  itemMaxWidth = 300,
}: Props) => {
  const [wrapper, setWrapper] = useState<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLaptopAndAbove = useIsLaptop();

  const { scrollX } = useScroll({
    container: scrollContainerRef,
  });

  const itemsCount = items.length;
  const getItemWidth = useCallback(() => {
    return isLaptopAndAbove
      ? Math.min(
          itemMaxWidth,
          (wrapper?.clientWidth || 0) / itemsCount - (itemsCount - 1) * ITEM_GAP
        )
      : (wrapper?.clientWidth || 0) / 2 - ITEM_GAP;
  }, [wrapper, isLaptopAndAbove, itemsCount, itemMaxWidth]);
  const [itemWidth, setItemWidth] = useState(getItemWidth());

  useEffect(() => {
    const recomputeItemWidth = () => {
      setItemWidth(getItemWidth());
    };
    recomputeItemWidth();
    window.addEventListener('resize', recomputeItemWidth);
    return () => window.removeEventListener('resize', recomputeItemWidth);
  }, [getItemWidth]);

  const selectedItemIndex = items.findIndex(
    item => item.key === selectedItemKey
  );

  useEffect(() => {
    if (typeof selectedItemIndex !== 'number') {
      return;
    }
    scrollContainerRef.current?.scrollTo({
      left: (itemWidth + ITEM_GAP) * selectedItemIndex,
      behavior: 'smooth',
    });
  }, [selectedItemIndex, itemWidth]);

  const handleScrollEnd = useCallback(
    (e: Event) => {
      const { scrollLeft } = e.target as HTMLDivElement;
      const index = Math.round(scrollLeft / (itemWidth + ITEM_GAP));
      const itemKey = items[index]?.key;
      if (itemKey) {
        onScrollToItem(itemKey);
      }
    },
    [itemWidth, items, onScrollToItem]
  );
  const debouncedHandleScrollEnd = useDebouncedCallback(handleScrollEnd);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    scrollContainer?.addEventListener('scrollend', handleScrollEnd);
    return () => {
      scrollContainer?.removeEventListener('scrollend', handleScrollEnd);
    };
  }, [handleScrollEnd]);

  return (
    <Wrapper
      style={
        {
          '--item-width': `${itemWidth}px`,
          '--item-aspect-ratio': itemAspectRatio,
        } as CSSProperties
      }
      ref={ref => setWrapper(ref)}
    >
      <WithScrollButtons
        scrollContainerRef={scrollContainerRef}
        wrapperRef={{ current: wrapper }}
        itemGap={ITEM_GAP_IN_UNIT}
        contained={isLaptopAndAbove ? 'calc(50% - var(--item-width))' : 4}
        overhangSize={8}
        scrollStep="item"
      >
        <ScrollWrapper
          ref={scrollContainerRef}
          onScroll={e => {
            if ('onscrollend' in window) {
              return;
            }
            //handle safari where onscrollend is not supported
            debouncedHandleScrollEnd(e as unknown as Event);
          }}
        >
          {items.map((item, index) => {
            const animationSteps = [
              (index - 2) * (itemWidth + ITEM_GAP) - 1,
              (index - 1) * (itemWidth + ITEM_GAP),
              index * (itemWidth + ITEM_GAP),
              (index + 1) * (itemWidth + ITEM_GAP),
              (index + 2) * (itemWidth + ITEM_GAP) + 1,
            ];
            return (
              <ItemWrapper
                key={item.key}
                className={classNames({
                  selected: item.key === selectedItemKey,
                })}
                onClick={() => {
                  onItemClick?.(item.key);
                }}
                style={{
                  opacity: scrollX.to(
                    animationSteps,
                    [0.8, 0.8, 1, 0.8, 1, 0.8]
                  ),
                  filter: scrollX
                    .to(animationSteps, [2, 2, 0, 2, 2])
                    .to(value => `blur(${value}px)`),
                  transform: scrollX
                    .to(animationSteps, [0.8, 0.8, 1, 0.8, 0.8])
                    .to(value => `scale(${value})`),
                }}
              >
                {children?.({
                  key: item.key,
                  pictureUrl: item.pictureUrl,
                }) || <img src={item.pictureUrl} alt="" />}
                {!!withShadow && <Shadow />}
              </ItemWrapper>
            );
          })}
        </ScrollWrapper>
      </WithScrollButtons>
    </Wrapper>
  );
};
